import { api } from '../../api.js';
import { els, setStatus } from './dom.js';
import { buildPromptFromListing, getListingsFromResponse } from './listing-data.js';
import { renderBoundListing, renderListingResults, renderPromptSource } from './view.js';

export function createListingController(state, { refreshSubmitState, loadVideos, loadListingImages } = {}) {
  let searchSeq = 0;

  function setListingSearchCollapsed(collapsed) {
    const section = els.listingSection();
    const toggle = els.listingToggle();
    if (section) section.classList.toggle('is-collapsed', Boolean(collapsed));
    if (toggle) {
      toggle.textContent = collapsed ? '更换' : '收起';
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      toggle.title = collapsed ? '展开搜索并更换 Listing' : '收起 Listing 搜索区';
    }
  }

  function applyInheritedProject(pid, { label = '来自 Listing' } = {}) {
    state.newProductProjectId = pid;
    const select = document.getElementById('video-project-select');
    if (!select) return;
    if (![...select.options].some((o) => o.value === String(pid))) {
      const opt = document.createElement('option');
      opt.value = String(pid);
      opt.textContent = `项目 #${pid}（${label}）`;
      select.appendChild(opt);
    }
    select.value = String(pid);
    // Listing ownership freezes project context for this generation session.
    select.disabled = true;
    select.title = '项目已从 Listing 继承，不可更换';
  }

  function inheritProjectFromListing(listing) {
    const raw = listing?.newProductProjectId
      ?? listing?.new_product_project_id
      ?? null;
    const pid = raw != null && Number(raw) > 0 ? Number(raw) : null;
    if (!pid) return;
    // Upstream listing project always wins so generation matches listing ownership.
    if (!state.newProductProjectId || Number(state.newProductProjectId) !== pid) {
      applyInheritedProject(pid);
      return;
    }
    applyInheritedProject(pid);
  }

  function setListing(listing) {
    state.listing = listing || null;
    renderBoundListing(state.listing);
    if (state.listing) {
      inheritProjectFromListing(state.listing);
      setListingSearchCollapsed(true);
      const prompt = els.prompt();
      if (prompt && (!prompt.value.trim() || state.promptSource === 'listing')) {
        prompt.value = buildPromptFromListing(state.listing);
        state.promptSource = 'listing';
      }
      loadVideos?.();
      loadListingImages?.();
    } else {
      setListingSearchCollapsed(false);
    }
    renderPromptSource(state);
    refreshSubmitState?.();
  }

  async function selectListing(id) {
    try {
      setStatus('正在读取 Listing...');
      const detail = await api.getSavedListing(id);
      setListing(detail?.listing || detail);
      setStatus('');
    } catch (err) {
      setStatus(err?.message || '读取 Listing 失败', 'error');
    }
  }

  async function searchListings() {
    const seq = ++searchSeq;
    const query = els.listingQuery()?.value || '';
    try {
      const params = { size: 20, search: query.trim() };
      if (state.newProductProjectId) params.projectId = state.newProductProjectId;
      const resp = await api.getSavedListings(1, params);
      if (seq !== searchSeq) return;
      renderListingResults(getListingsFromResponse(resp), selectListing);
    } catch (err) {
      if (seq !== searchSeq) return;
      const host = els.listingResults();
      if (host) {
        const empty = document.createElement('div');
        empty.className = 'video-empty';
        empty.textContent = err?.message || 'Listing 加载失败';
        host.replaceChildren(empty);
      }
    }
  }

  async function loadIntentListing() {
    const params = new URLSearchParams(window.location.search);
    const listingId = params.get('fromListing');
    if (!listingId) {
      renderBoundListing(null);
      await searchListings();
      return;
    }
    try {
      setStatus('正在绑定 Listing...');
      const detail = await api.getSavedListing(listingId);
      setListing(detail?.listing || detail);
      setStatus('');
    } catch (err) {
      setStatus(err?.message || '绑定 Listing 失败', 'error');
      renderBoundListing(null);
      await searchListings();
    }
  }

  function bindSearchInput() {
    els.listingQuery()?.addEventListener('input', () => {
      window.clearTimeout(searchListings._timer);
      searchListings._timer = window.setTimeout(searchListings, 240);
    });
    els.listingToggle()?.addEventListener('click', () => {
      const collapsed = els.listingSection()?.classList.contains('is-collapsed');
      setListingSearchCollapsed(!collapsed);
    });
  }

  return {
    bindSearchInput,
    loadIntentListing,
    searchListings,
    selectListing,
    setListing,
  };
}
